import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  Vibration,
  Platform,
  Image,
  ScrollView,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { Audio } from 'expo-av';
import * as Haptics from 'expo-haptics';
import ChessClockSettings from '../components/ChessClockSettings';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';
import { useAuth } from '../services/AuthContext';
import { getGameById, updateGame } from '../services/database';
import { useToast } from '../contexts/ToastContext';

const { height } = Dimensions.get('window');

const ChessClockScreen = ({ route, navigation }) => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const { appUser } = useAuth();
  const { showToast } = useToast();
  
  // Game data from route params
  const gameId = route?.params?.gameId;
  const opponentUser = route?.params?.opponentUser;
  const isPlayer1Param = route?.params?.isPlayer1;
  
  // Game state
  const [game, setGame] = useState(null);
  const [isPlayer1, setIsPlayer1] = useState(isPlayer1Param ?? true);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [opponentInfo, setOpponentInfo] = useState(opponentUser || null);

  // Time settings
  const [player1InitialTime, setPlayer1InitialTime] = useState(180); // seconds
  const [player2InitialTime, setPlayer2InitialTime] = useState(180);
  const [increment, setIncrement] = useState(0);

  // Current time remaining (in milliseconds for precision)
  const [player1Time, setPlayer1Time] = useState(180000); // milliseconds
  const [player2Time, setPlayer2Time] = useState(180000); // milliseconds

  // Game state
  const [activePlayer, setActivePlayer] = useState(null); // null, 1, or 2
  const [isPaused, setIsPaused] = useState(true);
  const [player1Flagged, setPlayer1Flagged] = useState(false);
  const [player2Flagged, setPlayer2Flagged] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false); // Hidden by default

  const intervalRef = useRef(null);
  const soundRef = useRef(null);
  const lastUpdateRef = useRef(Date.now());

  // Load game data if gameId provided
  useEffect(() => {
    console.log('🎮 ChessClockScreen route params:', {
      gameId: route?.params?.gameId,
      opponentUser: route?.params?.opponentUser,
      isPlayer1: route?.params?.isPlayer1,
    });
    
    if (gameId) {
      loadGameData();
    }
  }, [gameId]);

  // Debug: Log opponent header visibility
  useEffect(() => {
    console.log('🔍 Clock screen opponent header visibility:', {
      hasGame: !!game,
      hasOpponentInfo: !!opponentInfo,
      hasOpponentUser: !!opponentUser,
      gameId: gameId,
      shouldShowHeader: !!(game && opponentInfo),
    });
  }, [game, opponentInfo, opponentUser, gameId]);

  const loadGameData = async () => {
    try {
      const gameData = await getGameById(gameId);
      setGame(gameData);
      
      // Determine if I'm player 1
      const amPlayer1 = gameData.player1_id === appUser?.id;
      setIsPlayer1(amPlayer1);
      
      // Load opponent info from game data if not in route params
      if (!opponentInfo && gameData) {
        const opponent = amPlayer1 ? gameData.player2 : gameData.player1;
        if (opponent) {
          setOpponentInfo(opponent);
        }
      }
      
      // Check if game has started
      if (gameData.started_at) {
        setGameStarted(true);
        console.log('✅ Game started detected in Clock screen');
      } else {
        setGameStarted(false);
      }
      
      // Check if game has ended
      if (gameData.ended_at) {
        setGameEnded(true);
      } else {
        setGameEnded(false);
      }
    } catch (error) {
      console.error('Error loading game data:', error);
      showToast('Failed to load game data', 'error');
    }
  };

  // Poll for game state changes
  useEffect(() => {
    if (!gameId) return;
    
    const gameStateInterval = setInterval(async () => {
      try {
        const gameData = await getGameById(gameId);
        
        // Check if game just started
        if (gameData.started_at && !gameStarted) {
          setGameStarted(true);
          console.log('✅ Game started detected via polling');
        }
        
        // Check if game ended
        if (gameData.ended_at && !gameEnded) {
          setGameEnded(true);
        }
      } catch (error) {
        console.error('Error polling game state:', error);
      }
    }, 2000);
    
    return () => clearInterval(gameStateInterval);
  }, [gameId, gameStarted, gameEnded]);

  // Timer logic - update every 100ms for smooth countdown
  useEffect(() => {
    if (!isPaused && activePlayer && !player1Flagged && !player2Flagged) {
      lastUpdateRef.current = Date.now();
      
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = now - lastUpdateRef.current;
        lastUpdateRef.current = now;

        if (activePlayer === 1) {
          setPlayer1Time((prevTime) => {
            const newTime = Math.max(0, prevTime - elapsed);
            if (newTime <= 0) {
              // Player 1 flagged
              handleFlag(1);
              return 0;
            }
            return newTime;
          });
        } else if (activePlayer === 2) {
          setPlayer2Time((prevTime) => {
            const newTime = Math.max(0, prevTime - elapsed);
            if (newTime <= 0) {
              // Player 2 flagged
              handleFlag(2);
              return 0;
            }
            return newTime;
          });
        }
      }, 100); // Update every 100ms for smooth countdown
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, activePlayer, player1Flagged, player2Flagged]);

  const handleFlag = async (player) => {
    // Stop the clock
    setIsPaused(true);
    setActivePlayer(null);
    
    if (player === 1) {
      setPlayer1Flagged(true);
    } else {
      setPlayer2Flagged(true);
    }

    // If this is part of a game, mark it as ended
    if (gameId) {
      try {
        const winnerId = player === 1 
          ? (isPlayer1 ? game?.player2_id : game?.player1_id)
          : (isPlayer1 ? game?.player1_id : game?.player2_id);
        
        await updateGame(gameId, {
          ended_at: new Date().toISOString(),
          winner_id: winnerId,
        });
        
        setGameEnded(true);
        await loadGameData();
      } catch (error) {
        console.error('Error updating game on flag:', error);
      }
    }

    // Play sound
    // TODO: Add flag-sound.mp3 to assets folder for audio feedback
    try {
      // For now, we'll skip the sound if the file doesn't exist
      // You can add a flag-sound.mp3 file to /assets/ folder later
      if (Platform.OS === 'ios') {
        // On iOS, we can use a system sound as fallback
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      }
    } catch (error) {
      console.log('Sound not available:', error);
    }

    // Vibrate
    if (Platform.OS === 'ios') {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    } else {
      Vibration.vibrate([0, 500, 200, 500]);
    }
  };

  const handlePlayerTap = (player) => {
    if (player1Flagged || player2Flagged || isPaused) return;

    if (activePlayer === player) {
      // Switch to other player and add increment (convert seconds to milliseconds)
      if (player === 1) {
        setPlayer1Time((prev) => prev + (increment * 1000));
        setActivePlayer(2);
      } else {
        setPlayer2Time((prev) => prev + (increment * 1000));
        setActivePlayer(1);
      }
      
      // Reset last update time for accurate elapsed tracking
      lastUpdateRef.current = Date.now();
      
      // Haptic feedback on tap
      if (Platform.OS === 'ios') {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      }
    }
  };

  const handleStart = () => {
    if (!activePlayer) {
      setActivePlayer(1); // Start with player 1
    }
    lastUpdateRef.current = Date.now();
    setIsPaused(false);
  };

  const handlePause = () => {
    setIsPaused(true);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleReset = () => {
    setIsPaused(true);
    setActivePlayer(null);
    setPlayer1Time(player1InitialTime * 1000); // Convert seconds to milliseconds
    setPlayer2Time(player2InitialTime * 1000);
    setPlayer1Flagged(false);
    setPlayer2Flagged(false);
    lastUpdateRef.current = Date.now();
  };

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.ceil(milliseconds / 1000); // Round up to show remaining second
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const applySettings = (settings) => {
    console.log('Applying settings:', settings);
    setPlayer1InitialTime(settings.player1Time);
    setPlayer2InitialTime(settings.player2Time);
    setPlayer1Time(settings.player1Time * 1000); // Convert to milliseconds
    setPlayer2Time(settings.player2Time * 1000);
    setIncrement(settings.increment);
    setIsPaused(true);
    setActivePlayer(null);
    setPlayer1Flagged(false);
    setPlayer2Flagged(false);
    lastUpdateRef.current = Date.now();
  };

  const handleOpenSettings = () => {
    console.log('Opening settings modal');
    setSettingsVisible(true);
  };

  const handleCloseSettings = () => {
    console.log('Closing settings modal');
    setSettingsVisible(false);
  };

  // Dynamic styles based on theme
  const dynamicStyles = {
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    playerArea: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: isDarkMode ? '#2D2D2D' : '#e0e0e0',
      borderWidth: 0,
      minHeight: 200,
    },
    activePlayer: {
      backgroundColor: '#94EB98',
    },
    flaggedPlayer: {
      backgroundColor: '#FF4444',
    },
    timeText: {
      fontSize: 72,
      fontWeight: 'bold',
      color: colors.text,
      fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
      letterSpacing: 4,
    },
    flaggedText: {
      color: 'white',
    },
    activeTimeText: {
      color: '#1A1A1A',
    },
    playerLabel: {
      fontSize: 20,
      color: colors.textSecondary,
      marginTop: 12,
      fontWeight: '500',
    },
    activePlayerLabel: {
      color: '#1A1A1A',
    },
    controlsContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 20,
      paddingHorizontal: 20,
      backgroundColor: colors.surface,
      borderTopWidth: 1,
      borderBottomWidth: 1,
      borderColor: colors.border,
      minHeight: 100,
    },
    controlButton: {
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: isDarkMode ? '#3D3D3D' : '#f0f0f0',
      justifyContent: 'center',
      alignItems: 'center',
      elevation: 2,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      marginHorizontal: 12,
    },
    controlButtonIcon: {
      color: colors.text,
    },
    opponentHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    opponentInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      flex: 1,
    },
    opponentAvatar: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: colors.backgroundDark,
    },
    opponentAvatarPlaceholder: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    opponentDetails: {
      flex: 1,
    },
    opponentName: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    opponentRating: {
      fontSize: 14,
      color: colors.textSecondary,
      marginTop: 2,
    },
    startGameButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      backgroundColor: '#94EB98',
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 20,
    },
    startGameButtonText: {
      color: 'white',
      fontSize: 14,
      fontWeight: '600',
    },
    resultContainer: {
      padding: 16,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      zIndex: 10,
    },
    resultTitle: {
      fontSize: 18,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 12,
      textAlign: 'center',
    },
    resultButtons: {
      flexDirection: 'row',
      gap: 12,
      justifyContent: 'center',
    },
    resultButton: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 16,
      paddingHorizontal: 12,
      borderRadius: 12,
      minWidth: 100,
    },
    winButton: {
      backgroundColor: '#2E7D32',
    },
    drawButton: {
      backgroundColor: '#666',
    },
    lossButton: {
      backgroundColor: '#d32f2f',
    },
    resultButtonText: {
      color: 'white',
      fontSize: 14,
      fontWeight: '600',
    },
  };

  // Report game result
  const handleReportResult = async (result) => {
    if (!gameId) return;
    
    try {
      const resultField = isPlayer1 ? 'player1_reported_result' : 'player2_reported_result';
      await updateGame(gameId, {
        [resultField]: result,
      });
      
      showToast('Result reported! Waiting for opponent...', 'info');
      
      // Poll to check if opponent also reported
      const checkInterval = setInterval(async () => {
        try {
          const updatedGame = await getGameById(gameId);
          
          const myResult = isPlayer1 
            ? updatedGame.player1_reported_result 
            : updatedGame.player2_reported_result;
          const opponentResult = isPlayer1 
            ? updatedGame.player2_reported_result 
            : updatedGame.player1_reported_result;
          
          if (opponentResult) {
            clearInterval(checkInterval);
            
            // Check if results match
            if (myResult === opponentResult) {
              // Results match - finalize game
              await updateGame(gameId, {
                ended_at: new Date().toISOString(),
                result: myResult,
              });
              
              showToast('Game completed!', 'success');
              setGameEnded(true);
              await loadGameData();
            } else {
              // Results don't match - dispute
              await updateGame(gameId, {
                ended_at: new Date().toISOString(),
                result: 'CANCELLED',
                dispute_resolved: false,
              });
              
              showToast('Results don\'t match. Game in dispute.', 'error');
              setGameEnded(true);
              await loadGameData();
            }
          }
        } catch (error) {
          console.error('Error checking opponent result:', error);
        }
      }, 2000);
      
      // Timeout after 60 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
      }, 60000);
      
    } catch (error) {
      console.error('Error reporting result:', error);
      showToast('Failed to report result', 'error');
    }
  };

  // Start the game (mark both players ready)
  const handleStartGame = async () => {
    if (!gameId) {
      // No game - just start the clock
      handleStart();
      return;
    }
    
    try {
      // Mark myself as ready
      const readyField = isPlayer1 ? 'player1_ready_to_start' : 'player2_ready_to_start';
      await updateGame(gameId, {
        [readyField]: true,
        started_at: new Date().toISOString(),
      });
      
      setGameStarted(true);
      handleStart(); // Start the clock
      showToast('Game started!', 'success');
    } catch (error) {
      console.error('Error starting game:', error);
      showToast('Failed to start game', 'error');
    }
  };

  return (
    <View style={dynamicStyles.container}>
      {/* Opponent Info Header (if game exists) */}
      {game && opponentInfo && (
        <View style={dynamicStyles.opponentHeader}>
          <View style={dynamicStyles.opponentInfo}>
            {opponentInfo.profilePictureUrl || opponentInfo.profile_picture_url ? (
              <Image 
                source={{ uri: opponentInfo.profilePictureUrl || opponentInfo.profile_picture_url }} 
                style={dynamicStyles.opponentAvatar}
              />
            ) : (
              <View style={[dynamicStyles.opponentAvatar, dynamicStyles.opponentAvatarPlaceholder]}>
                <Icon name="person" size={24} color={colors.textSecondary} />
              </View>
            )}
            <View style={dynamicStyles.opponentDetails}>
              <Text style={dynamicStyles.opponentName}>
                {opponentInfo.username || opponentInfo.club_name || 'Opponent'}
              </Text>
              <Text style={dynamicStyles.opponentRating}>
                Rating: {opponentInfo.inPersonRating || opponentInfo.in_person_rating || 1200}
              </Text>
            </View>
          </View>
          {!gameStarted && gameId && (
            <TouchableOpacity
              style={dynamicStyles.startGameButton}
              onPress={handleStartGame}
            >
              <Icon name="play-arrow" size={20} color="white" />
              <Text style={dynamicStyles.startGameButtonText}>Start Game</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Result Reporting - Always visible when game is started */}
      {gameStarted && game && !gameEnded && (
        <View style={dynamicStyles.resultContainer}>
          <Text style={dynamicStyles.resultTitle}>Report Result</Text>
          <View style={dynamicStyles.resultButtons}>
            <TouchableOpacity
              style={[dynamicStyles.resultButton, dynamicStyles.winButton]}
              onPress={() => handleReportResult(isPlayer1 ? 'PLAYER1_WIN' : 'PLAYER2_WIN')}
            >
              <Icon name="emoji-events" size={24} color="white" />
              <Text style={dynamicStyles.resultButtonText}>I Won</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[dynamicStyles.resultButton, dynamicStyles.drawButton]}
              onPress={() => handleReportResult('DRAW')}
            >
              <Icon name="people" size={24} color="white" />
              <Text style={dynamicStyles.resultButtonText}>Draw</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[dynamicStyles.resultButton, dynamicStyles.lossButton]}
              onPress={() => handleReportResult(isPlayer1 ? 'PLAYER2_WIN' : 'PLAYER1_WIN')}
            >
              <Icon name="close" size={24} color="white" />
              <Text style={dynamicStyles.resultButtonText}>I Lost</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Result Reporting (if game ended via timeout) */}
      {gameEnded && game && (
        <View style={dynamicStyles.resultContainer}>
          <Text style={dynamicStyles.resultTitle}>Game Ended - Report Result</Text>
          <View style={dynamicStyles.resultButtons}>
            <TouchableOpacity
              style={[dynamicStyles.resultButton, dynamicStyles.winButton]}
              onPress={() => handleReportResult(isPlayer1 ? 'PLAYER1_WIN' : 'PLAYER2_WIN')}
            >
              <Icon name="emoji-events" size={24} color="white" />
              <Text style={dynamicStyles.resultButtonText}>I Won</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[dynamicStyles.resultButton, dynamicStyles.drawButton]}
              onPress={() => handleReportResult('DRAW')}
            >
              <Icon name="people" size={24} color="white" />
              <Text style={dynamicStyles.resultButtonText}>Draw</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[dynamicStyles.resultButton, dynamicStyles.lossButton]}
              onPress={() => handleReportResult(isPlayer1 ? 'PLAYER2_WIN' : 'PLAYER1_WIN')}
            >
              <Icon name="close" size={24} color="white" />
              <Text style={dynamicStyles.resultButtonText}>I Lost</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Player 2 (Top - Rotated) */}
      <TouchableOpacity
        style={[
          dynamicStyles.playerArea,
          activePlayer === 2 && !player2Flagged && dynamicStyles.activePlayer,
          player2Flagged && dynamicStyles.flaggedPlayer,
        ]}
        onPress={() => handlePlayerTap(2)}
        activeOpacity={0.9}
      >
        <View style={styles.rotatedContent}>
          <Text
            style={[
              dynamicStyles.timeText,
              activePlayer === 2 && !player2Flagged && dynamicStyles.activeTimeText,
              player2Flagged && dynamicStyles.flaggedText,
            ]}
          >
            {formatTime(player2Time)}
          </Text>
          <Text style={[
            dynamicStyles.playerLabel, 
            activePlayer === 2 && !player2Flagged && dynamicStyles.activePlayerLabel,
            player2Flagged && dynamicStyles.flaggedText
          ]}>
            Player 2
          </Text>
        </View>
      </TouchableOpacity>

      {/* Controls (Middle) */}
      <View style={dynamicStyles.controlsContainer}>
        <TouchableOpacity
          style={dynamicStyles.controlButton}
          onPress={handleOpenSettings}
          activeOpacity={0.7}
        >
          <Icon name="settings" size={24} color={colors.text} />
        </TouchableOpacity>

        {isPaused && !player1Flagged && !player2Flagged ? (
          <TouchableOpacity
            style={[dynamicStyles.controlButton, styles.primaryButton]}
            onPress={gameId ? handleStartGame : handleStart}
          >
            <Icon name="play-arrow" size={28} color="white" />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={[dynamicStyles.controlButton, styles.primaryButton]}
            onPress={handlePause}
          >
            <Icon name="pause" size={28} color="white" />
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={dynamicStyles.controlButton}
          onPress={handleReset}
        >
          <Icon name="refresh" size={24} color={colors.text} />
        </TouchableOpacity>
      </View>

      {/* Player 1 (Bottom) */}
      <TouchableOpacity
        style={[
          dynamicStyles.playerArea,
          activePlayer === 1 && !player1Flagged && dynamicStyles.activePlayer,
          player1Flagged && dynamicStyles.flaggedPlayer,
        ]}
        onPress={() => handlePlayerTap(1)}
        activeOpacity={0.9}
      >
        <View>
          <Text
            style={[
              dynamicStyles.timeText,
              activePlayer === 1 && !player1Flagged && dynamicStyles.activeTimeText,
              player1Flagged && dynamicStyles.flaggedText,
            ]}
          >
            {formatTime(player1Time)}
          </Text>
          <Text style={[
            dynamicStyles.playerLabel,
            activePlayer === 1 && !player1Flagged && dynamicStyles.activePlayerLabel,
            player1Flagged && dynamicStyles.flaggedText
          ]}>
            Player 1
          </Text>
        </View>
      </TouchableOpacity>

      {/* Settings Modal */}
      {settingsVisible && (
        <ChessClockSettings
          visible={settingsVisible}
          onClose={handleCloseSettings}
          onApply={applySettings}
          currentSettings={{
            player1Time: player1InitialTime,
            player2Time: player2InitialTime,
            increment,
          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  rotatedContent: {
    transform: [{ rotate: '180deg' }],
    alignItems: 'center',
  },
  primaryButton: {
    backgroundColor: '#94EB98',
    width: 64,
    height: 64,
    borderRadius: 32,
    marginHorizontal: 12,
  },
});

export default ChessClockScreen;
